import {_axios} from '@/plugins/axios';
import router from '@/plugins/router';
import {ActionContext, ActionTree} from 'vuex';
import {AdvertState, AdvertTypeItems} from '@/store/modules/Advert/state';
import {RootState} from '@/store';
import {AxiosError, AxiosResponse} from 'axios';
import {
    Advert,
    AdvertAvailabilityType,
    AdvertCreationModification,
    AdvertDetailAPI,
    AdvertListAPI,
    AdvertPeriod,
} from '#/Advert';
import {SearchAPI} from '#/Search';
import i18n from '@/plugins/i18n';

const getDateWithDatepickerFormat = (date: Date): string => {
    const year = date.getFullYear();
    const month = date.getMonth() <= 8 ? `0${date.getMonth() + 1}` : date.getMonth() + 1;
    const day = date.getDate() <= 9 ? `0${date.getDate()}` : date.getDate();

    return `${year}-${month}-${day}`;
};

/**
 * Construit la période de l'objet :
 * - Si c'est une date ponctuelle ou une période de date
 * - Si c'est un créneau horaire
 *
 * @param startsAt
 * @param endsAt
 * @param availability
 *
 * @return {AdvertPeriod}
 */
export const getAdvertPeriod = (
    startsAt: string | null,
    endsAt: string | null,
    availability: AdvertAvailabilityType | null,
): AdvertPeriod => {
    const period: AdvertPeriod = {
        periodSelected: null,
        isDate: false,
        datesSelected: [],
    };

    if (startsAt !== null) {
        period.isDate = true;
        period.datesSelected = [];

        const start = new Date(startsAt);
        period.datesSelected.push(getDateWithDatepickerFormat(start));

        if (endsAt !== null) {
            const end = new Date(endsAt);
            period.datesSelected.push(getDateWithDatepickerFormat(end));
        }
    } else {
        period.isDate = false;
        period.datesSelected = [];
        if (availability !== null) period.periodSelected = availability;
    }
    return period;
};

export const advertToJS = (advert: AdvertDetailAPI): Advert => {
    const {
        archivedAt,
        updatedAt,
        createdAt,
        startsAt,
        endsAt,
        type,
        availability,
        publishedAt,
        ...rest
    } = advert;

    return {
        period: getAdvertPeriod(startsAt, endsAt, availability),
        availability,
        archivedAt: archivedAt ? new Date(archivedAt) : null,
        updatedAt: new Date(updatedAt),
        createdAt: new Date(createdAt),
        startsAt: startsAt ? new Date(startsAt) : null,
        endsAt: endsAt ? new Date(endsAt) : null,
        publishedAt: publishedAt ? new Date(publishedAt) : null,
        type: AdvertTypeItems[type],
        ...rest,
    };
};

/**
 * Convertit les dates et les types des annonces reçues de l'api list
 *
 * @param {Object} adverts
 * @returns {*}
 */
export const advertsToJS = (adverts: AdvertDetailAPI[]): Advert[] => {
    return adverts.map(advert => advertToJS(advert));
};

export const actions: ActionTree<AdvertState, RootState> = {
    /**
     * Dé-publication d'une annonce
     * avec une mise à jour éventuelle de ses attributs
     */
    archiveAdvert: ({dispatch}: ActionContext<AdvertState, RootState>, advert: Advert): Promise<void> => {
        return new Promise((resolve, reject) => {
            _axios.patch(`postings/${advert.id}/archive/`, advert)
                  .then(() => {
                      router.push({name: 'PersonalAdvertList', query: {archive: `${true}`}})
                            .then(() => {
                                resolve();
                            })
                            .catch(() => {
                                reject();
                            });
                  })
                  .catch((error: AxiosError) => {
                      dispatch('displayError', error.response, {root: true})
                          .then(() => {
                              resolve();
                          });
                  });
        });
    },

    /**
     * TODO: ajouter doc
     */
    changeSearchFieldStatus: ({commit}: ActionContext<AdvertState, RootState>, newStatus: boolean): void => {
        commit('CHANGE_FIELD_STATUS', newStatus);
    },

    /**
     * Ajout ou suppression de l'annonce dans la liste des annonces favorites de l'utilisateur
     */
    favoriteUpdate: (
        {commit, dispatch}: ActionContext<AdvertState, RootState>,
        {id, isFavorite}: Advert,
    ): Promise<void> => {
        return new Promise(resolve => {
            const toggleAction = isFavorite ? 'remove-from' : 'add-to';

            _axios.patch(`postings/${id}/${toggleAction}-favorites/`)
                  .then(() => {
                      commit('FAVORITE_TOGGLE', id);
                      resolve();
                  })
                  .catch((error: AxiosError) => {
                      dispatch('displayError', error.response, {root: true})
                          .then(() => {
                              resolve();
                          });
                  });
        });
    },

    /**
     * Charge une annonce spécifique avec son ID.
     */
    loadAdvert: ({commit, dispatch}: ActionContext<AdvertState, RootState>, advertId): Promise<void> => {
        return new Promise(resolve => {
            _axios.get(`postings/${advertId}/`)
                  .then((response: AxiosResponse<AdvertDetailAPI>) => {
                      commit('LOAD_ADVERT', advertToJS(response.data));
                      resolve();
                  })
                  .catch(error => {
                      dispatch('displayError', error.response, {root: true})
                          .then(() => {
                              resolve();
                          });
                  });
        });
    },

    /**
     * Charge les annonces pour la page de liste des annonces avec les filtres donnés
     * ou les annonces avec les filtres personnalisés
     */
    loadAdverts: (
        {commit, dispatch}: ActionContext<AdvertState, RootState>,
        {filters = null, fromDefault = false},
    ): Promise<void> => {
        return new Promise(resolve => {
            let url = 'postings/';

            if (fromDefault) {
                url += 'default-filter/';
            }
            if (filters) {
                for (const [index, [keyName, value]] of Object.entries(filters)
                                                              .entries()) {
                    url += (index === 0 ? '?' : '&') + `${keyName}=${value}`;
                }
            }

            _axios.get(url)
                  .then((response: AxiosResponse<AdvertListAPI>) => {
                      const {next, results: advertsLoaded} = response.data;
                      const urlNext = next?.match(/\/api\/(.*)/);
                      commit('LOAD_NEXT_PAGE', urlNext ? urlNext[1] : '');
                      commit('SAVE_PAGE_NUMBER', 1);
                      commit('EMPTY_ADVERTS');
                      commit('LOAD_ADVERTS', advertsToJS(advertsLoaded));
                      resolve();
                  })
                  .catch(error => {
                      dispatch('displayError', error.response, {root: true})
                          .then(() => {
                              resolve();
                          });
                  });
        });
    },

    /**
     * Charge les annonces via l'URL nextPage renvoyé par ES.
     */
    loadNextPage: ({commit, dispatch, state}: ActionContext<AdvertState, RootState>): Promise<void> => {
        return new Promise(resolve => {
            if (state.urlNextPage) {
                return _axios.get(state.urlNextPage)
                             .then(response => {
                                 const {next, results: advertsLoaded} = response.data;
                                 const urlNext = next?.match(/\/api\/(.*)/);
                                 commit('LOAD_NEXT_PAGE', urlNext ? urlNext[1] : '');
                                 commit('SAVE_PAGE_NUMBER', state.pageNumber + 1);
                                 commit('LOAD_ADVERTS', advertsToJS(advertsLoaded));
                                 resolve();
                             })
                             .catch(error => {
                                 dispatch('displayError', error.response, {root: true})
                                     .then(() => {
                                         resolve();
                                     });
                             });
            } else {
                resolve();
            }
        });
    },

    /**
     * Envoie une requête pour faire une recherche sur un mot.
     */
    loadSearchResult: (
        {commit, dispatch, state}: ActionContext<AdvertState, RootState>,
        {searchTerms, actualPath, loadNextPage, facetTerms}: {
            searchTerms: string,
            actualPath: string,
            loadNextPage: string,
            facetTerms: { type: 'searchTerms', value: string }
        },
    ): Promise<void> => {
        return new Promise((resolve, reject) => {
            // Si ce n'est pas un chargement lié à la page infinie on réinitialise le tableau des résultats.
            if (!loadNextPage) {
                commit('INIT_SEARCH');
            }

            // Si c'est une recherche rejouée (page infinie) on récupère l'URL de la page suivante stocké dans le state
            const axiosUrl = loadNextPage ? state.urlNextPage : `search/?page=1`;

            // Crée un objet pour le corps de la requête avec les termes cherchés
            const requestBody = {
                searchTerms: searchTerms || state.searchOptions.lastTerm,
            };

            // Si c'est une recherche à facette, on ajoute la facette
            if (facetTerms !== undefined) requestBody[facetTerms.type] = facetTerms.value;

            if (axiosUrl !== null) {
                return _axios.post(axiosUrl, requestBody)
                             .then((response: AxiosResponse<SearchAPI>) => {
                                 const {hits: results, suggest: suggests} = response.data;
                                 const {
                                     types: {buckets: facetsType},
                                     categories: {buckets: facetsCategory},
                                 } = response.data.aggregations;

                                 const pageNumberTotal = Math.ceil(
                                     results.total.value / parseInt(process.env.VUE_APP_PAGINATION_LIMIT),
                                 );

                                 const nextUrl = response.config.url?.match(/\?page=([0-9]+)/);
                                 commit('SAVE_PAGE_NUMBER', parseInt(nextUrl ? nextUrl[1] : ''));

                                 state.pageNumber < pageNumberTotal
                                     ? commit('LOAD_NEXT_PAGE', `search/?page=${state.pageNumber + 1}`)
                                     : commit('LOAD_NEXT_PAGE', null);

                                 commit('SAVE_SEARCH_OPTIONS', {
                                     facets: {
                                         category: searchTerms === null ? null : facetsCategory,
                                         type: searchTerms === null ? null : facetsType,
                                     },
                                     isSpecify: facetTerms !== undefined,
                                     resultNumber: searchTerms === null ? null : results.total.value,
                                     results: results.hits.map(hit => ({
                                         type: AdvertTypeItems[hit.Source.typeId],
                                         id: hit.Source.id,
                                         author: hit.Source.author,
                                         sourceTitle: hit.Source.title,
                                         sourceContent: hit.Source.content,
                                         updatedAt: new Date(hit.Source.updatedAt),
                                         ...hit.highlight,
                                     })),
                                     searchTerms,
                                     suggests: suggests.suggest[0].options,
                                 });

                                 if (actualPath !== 'SearchResult') {
                                     router.push({name: 'SearchResult'})
                                           .then(() => {
                                               resolve();
                                           })
                                           .catch(() => {
                                               reject();
                                           });
                                 } else {
                                     resolve();
                                 }
                             })
                             .catch(error => {
                                 dispatch('displayError', error.response, {root: true})
                                     .then(() => {
                                         resolve();
                                     });
                             });
            }

            resolve();
        });
    },

    /**
     * Permet à un administrateur de modérer une annonce.
     */
    moderateAdvert: (
        {dispatch}: ActionContext<AdvertState, RootState>,
        {id, self = false},
    ): Promise<void> => {
        return new Promise((resolve, reject) => {
            _axios.delete(`postings/${id}/`)
                  .then(() => {
                      router.push({name: self ? 'PersonalAdvertList' : 'DefaultList'})
                            .then(() => {
                                resolve();
                            })
                            .catch(() => {
                                reject();
                            });
                  })
                  .catch(error => {
                      dispatch('displayError', error.response, {root: true})
                          .then(() => {
                              resolve();
                          });
                  });
        });
    },

    reportAdvert: ({dispatch}: ActionContext<AdvertState, RootState>, advertId: number): Promise<void> => {
        return new Promise(resolve => {
            _axios.get(`/api/postings/${advertId}/report/`)
                  .then(() => {
                      dispatch(
                          'displaySnackBar',
                          {
                              text: i18n.t('advert.detail.abuse.dialog.confirmation') as string,
                              type: 'valid',
                          },
                      )
                          .then(() => {
                              resolve();
                          });
                  });
        });
    },

    /**
     * Utilisée pour vider l'objet advert dans le state si on est sur la création d'une nouvelle annonce.
     */
    resetAdvert: ({commit}: ActionContext<AdvertState, RootState>): Promise<void> => {
        return new Promise(resolve => {
            commit('INIT_ADVERT');
            resolve();
        });
    },

    /**
     * Création d'une annonce, soit comme brouillon soit comme annonce publiée
     */
    saveAdvert: (
        {dispatch}: ActionContext<AdvertState, RootState>,
        advert: AdvertCreationModification,
    ): Promise<void> => {
        return new Promise((resolve, reject) => {
            _axios.post(`postings/`, advert)
                  .then((response: AxiosResponse<AdvertDetailAPI>) => {
                      response.data.isPublished
                          ? router.push({name: 'AdvertList'})
                                  .then(() => {
                                      resolve();
                                  })
                                  .catch(() => {
                                      reject();
                                  })
                          : router.push({name: 'PersonalAdvertList'})
                                  .then(() => {
                                      resolve();
                                  })
                                  .catch(() => {
                                      reject();
                                  });
                  })
                  .catch(error => {
                      dispatch('displayError', error.response, {root: true})
                          .then(() => {
                              resolve();
                          });
                  });
        });
    },

    /**
     * Sauvegarde les mots recherchées dans le store.
     */
    saveSearchTerm: ({commit}: ActionContext<AdvertState, RootState>, searchTerm): void => {
        commit('SAVE_SEARCH_TERM', searchTerm);
    },

    /**
     * Sauvegarde le type sélectionné pour la navigation.
     */
    selectTypeFilter: ({commit}: ActionContext<AdvertState, RootState>, selectedType): void => {
        commit(
            'SELECT_TYPE_FILTER',
            selectedType ? AdvertTypeItems[parseInt(selectedType)] : null,
        );
    },

    /**
     * Mise à jour d'une annonce
     */
    updateAdvert: (
        {dispatch}: ActionContext<AdvertState, RootState>,
        advert: AdvertCreationModification,
    ): Promise<void> => {
        return new Promise((resolve, reject) => {
            _axios.patch(`postings/${advert.id}/`, advert)
                  .then((response: AxiosResponse<AdvertDetailAPI>) => {
                      response.data.isPublished
                          ? router.push({name: 'AdvertList'})
                                  .then(() => {
                                      resolve();
                                  })
                                  .catch(() => {
                                      reject();
                                  })
                          : router.push({name: 'PersonalAdvertList'})
                                  .then(() => {
                                      resolve();
                                  })
                                  .catch(() => {
                                      reject();
                                  });
                  })
                  .catch(error => {
                      dispatch('displayError', error.response, {root: true})
                          .then(() => {
                              resolve();
                          });
                  });
        });
    },
};
